import { NodeType } from "../models/types.ts";

// Message-type nodes whose plain body-level connector can only ever reach a Smart Delay.
//
// Once one of these has sent its content the engine stops and waits for an inbound reply:
// edges are followed by matching sourceHandle against the reply id of a button/list tap, and
// the body connector carries no such id. The single exception is scheduleNextDelayIfAny in
// WhatsappHelperService, which follows a node's own body wire if — and only if — it lands on a
// SMART_DELAY. So wiring one of these straight into another message node draws a line that
// looks connected but silently dead-ends at runtime; refuse it at the canvas instead.
//
// Deliberately NOT listed, all of which need an unrestricted body wire:
//  - FLOW_START — its body wire is the flow's entry point into the first message node.
//  - QUESTION / MEDIA / ADDRESS / LOCATION — they consume the customer's next message and then
//    continue via PrevSentMessage.targetNodeId.
//  - ADD_TAG / ATTRIBUTE / CONDITION / API_REQUEST / FLOW / GOOGLE_SHEETS — these self-chain,
//    calling straight on into their body target as soon as they have run.
//  - SMART_DELAY — its body wire is what resumes the flow once the wait is over.
//  - LIST / MULTI_PRODUCT ("List" and "Option List") — exempted on request: their body wire is
//    left free to point at any node, so the canvas no longer forces a Smart Delay in between.
export const DELAY_ONLY_BODY_SOURCES: NodeType[] = [
    "TEXT_BUTTON",
    "MEDIA_BUTTON",
    "SINGLE_PRODUCT",
    "PRODUCT_LIST",
    "TEMPLATE",
    "CAROUSEL",
];

// A connection dragged from the node body itself carries no handle id, while buttons and list
// rows each carry their own. Saved flows are normalised on the way out (see Canvas#onSave), so
// a persisted body edge instead has sourceHandle === the source node id; treat both as "body".
export const isBodyHandle = (sourceHandle?: string | null, sourceId?: string | null) =>
    !sourceHandle || sourceHandle === sourceId;

// The "no reply" connector on a FLOW_START template. Namespaced by Meta template id because the
// backend matches edges by sourceHandle text across every flow of the business at once — a shared
// label like "No Reply" would be picked up by an unrelated flow's edge.
export const NO_REPLY_HANDLE_SUFFIX = ":NO_REPLY";

export const noReplyHandleFor = (templateId: string) => `${templateId}${NO_REPLY_HANDLE_SUFFIX}`;

export const isNoReplyHandle = (sourceHandle?: string | null) =>
    !!sourceHandle && sourceHandle.endsWith(NO_REPLY_HANDLE_SUFFIX);

export function isBodyLinkAllowed(params: {
    sourceType?: NodeType;
    targetType?: NodeType;
    sourceHandle?: string | null;
    sourceId?: string | null;
}): boolean {
    const { sourceType, targetType, sourceHandle, sourceId } = params;

    // Checked before the body-handle test below, which would otherwise let this through as an
    // ordinary handled connector. The wait itself is what the follow-up branch hangs off.
    if (isNoReplyHandle(sourceHandle)) return targetType === "SMART_DELAY";

    // Button and list-row connectors are unaffected — they route on a real reply id.
    if (!isBodyHandle(sourceHandle, sourceId)) return true;
    if (!sourceType || !DELAY_ONLY_BODY_SOURCES.includes(sourceType)) return true;

    return targetType === "SMART_DELAY";
}
